import { useCallback, useEffect, useRef, useState } from "react";

const COOLDOWN_SECONDS = 30;

/**
 * Manages a 30-second cooldown for resending an OTP code.
 *
 * Returns:
 * - `secondsLeft`: 0 when idle, > 0 while cooling down
 * - `startCooldown`: call after successfully sending a code
 * - `canResend`: true when the user is allowed to click Resend
 */
export function useResendCooldown() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCooldown = useCallback(() => {
    clear();
    setSecondsLeft(COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clear();
          return 0;
        }
        return prev - 1;
      });
    }, 1_000);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return {
    secondsLeft,
    canResend: secondsLeft === 0,
    startCooldown,
  };
}
